import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowSquareOut,
  CaretLeft,
  ChartBar,
  ChatText,
  CheckCircle,
  Circle,
  CornersOut,
  Exam,
  Eye,
  FileText,
  Flask,
  FloppyDisk,
  ListBullets,
  ListChecks,
  Monitor,
  NotePencil,
  ShieldWarning,
  Star,
  VideoCamera,
} from '@phosphor-icons/react';
import type {
  Chapter,
  Lab,
  Lesson,
  Module,
  ModuleView,
  Section,
  StudyAttempt,
  StudyQuestionPublic,
} from '@cvs/shared';
import { api } from '../lib/api';
import { useCourse } from '../lib/course';
import { useAsync } from '../lib/useAsync';
import { formatDuration } from '../lib/format';
import { RichText } from '../components/RichText';
import { ClassDiscussion, ClassFeedback } from '../components/ClassEngagement';
import { Card, ErrorState, LoadingState, Modal, Pill, ProgressBar, SectionHeading, Spinner, Tabs, cx } from '../components/ui';

// pdf.js-based slide deck — lazy so pdf.js stays out of the main bundle.
const PdfDeck = lazy(() => import('../components/PdfDeck'));

// Inline-viewable file detection (extension taken from the stored blob/URL).
const PDF_RE = /\.pdf(\?|#|$)/i;
const OFFICE_RE = /\.(pptx?|ppsx?|docx?|xlsx?)(\?|#|$)/i;
const VIDEO_FILE_RE = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i;

/** Normalize common watch URLs to embeddable player URLs (YouTube/Vimeo). */
function toEmbedUrl(url: string): string {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url; // already an embed URL (Stream/SharePoint/etc.)
}

/** Microsoft's Office viewer renders PPTX/DOCX/XLSX inline via the SAS link. */
function officeEmbedUrl(fileUrl: string): string {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}

interface ClassData {
  module: Module;
  materials: Lesson[];
  quizId: string | null;
  labs: Lab[];
  completedIds: Set<string>;
  studyQuestions: StudyQuestionPublic[];
  studyAttempts: StudyAttempt[];
}

// ── Resolution: pick the class to show ───────────────────────────────────────
async function resolveClassId(lessonParam: string | null, moduleParam: string | null): Promise<string> {
  if (moduleParam) return moduleParam;
  if (lessonParam) {
    const { lesson } = await api.lesson(lessonParam);
    return lesson.moduleId;
  }
  const { modules } = await api.program();
  const pick =
    modules.find((m: ModuleView) => m.state === 'in-progress') ??
    modules.find((m: ModuleView) => m.state === 'available') ??
    modules[0];
  if (!pick) throw new Error('No classes are available yet.');
  return pick.id;
}

// ── Screen ───────────────────────────────────────────────────────────────────
export function CoursePlayer() {
  const { classId: classIdParam, lessonId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { coursePath } = useCourse();

  // Class id comes from the readable /classes/:classId path (or, for back-compat,
  // ?module=); a lesson id (old /course/:lessonId or ?lesson=) resolves to its class.
  const lessonParam = lessonId ?? params.get('lesson');
  const classParam = classIdParam ?? params.get('module');
  const routeKey = classParam ?? lessonParam ?? 'default';

  const state = useAsync(async () => {
    const classId = await resolveClassId(lessonParam, classParam);
    const [{ module, lessons, quizId, labs }, progress, program, study] = await Promise.all([
      api.moduleLessons(classId),
      api.progress(),
      api.program(),
      // Non-fatal: a study-questions failure must never break the class page.
      api.studyQuestions(classId).catch(() => ({
        questions: [] as StudyQuestionPublic[],
        attempts: [] as StudyAttempt[],
      })),
    ]);
    const done = new Set(progress.filter((p) => p.completed).map((p) => p.lessonId));
    const data: ClassData = {
      module,
      materials: lessons,
      quizId,
      labs,
      completedIds: done,
      studyQuestions: study.questions,
      studyAttempts: study.attempts,
    };
    return { data, sections: program.sections, siblings: program.modules };
  }, [routeKey]);

  if (state.loading) return <LoadingState label="Loading class…" />;
  if (state.error || !state.data)
    return <ErrorState message={state.error ?? 'Failed to load'} onRetry={state.reload} />;

  return (
    <Player
      key={routeKey}
      cls={state.data.data}
      sections={state.data.sections}
      siblings={state.data.siblings}
      onNavigate={(id) => navigate(coursePath(`classes/${id}`))}
    />
  );
}

// ── Inner player ─────────────────────────────────────────────────────────────
function Player({
  cls,
  sections,
  siblings,
  onNavigate,
}: {
  cls: ClassData;
  sections: Section[];
  siblings: ModuleView[];
  onNavigate: (moduleId: string) => void;
}) {
  const { module, materials, quizId, labs, studyQuestions, studyAttempts } = cls;
  const { coursePath } = useCourse();
  const [searchParams] = useSearchParams();
  const byType = new Map(materials.map((m) => [m.type, m]));
  const recording = byType.get('recording') ?? null;
  const slides = byType.get('slides') ?? null;
  const transcript = byType.get('transcript') ?? null;
  const summary = byType.get('summary') ?? null;
  const notes = byType.get('notes') ?? null;
  const infographic = byType.get('infographic') ?? null;
  const incident = byType.get('incident') ?? null;

  const [completed, setCompleted] = useState(
    materials.length > 0 && materials.every((m) => cls.completedIds.has(m.id)),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Open on the tab named in ?tab= (e.g. the "Practise" links from Assessments).
  const [tab, setTab] = useState(() => searchParams.get('tab') ?? 'overview');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [showAllClasses, setShowAllClasses] = useState(false);

  const section = sections.find((s) => s.id === module.sectionId) ?? null;
  const sectionClasses = siblings.filter((m) => m.sectionId === module.sectionId);
  // Rail: previous / current / next within this section, plus a completion count.
  const railClasses = sectionClasses.length > 0 ? sectionClasses : siblings;
  const currentIdx = railClasses.findIndex((m) => m.id === module.id);
  const prevClass = currentIdx > 0 ? railClasses[currentIdx - 1] : null;
  const nextRailClass = currentIdx >= 0 && currentIdx < railClasses.length - 1 ? railClasses[currentIdx + 1] : null;
  const railDone = railClasses.filter((m) => m.state === 'completed').length;

  // Report how far the learner has watched the recording (best-effort, silent).
  function reportWatch(sec: number) {
    if (!recording) return;
    void api.saveProgress({ lessonId: recording.id, watchedSec: Math.round(sec) }).catch(() => {});
  }

  async function markComplete() {
    setSaving(true);
    setSaveError(null);
    try {
      // Completing a class completes all of its materials.
      for (const m of materials) {
        await api.saveProgress({ lessonId: m.id, completed: true, lastPositionSec: m.durationSec });
      }
      setCompleted(true);
      setFeedbackOpen(true); // prompt for a rating right after completion
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Could not save progress.');
    } finally {
      setSaving(false);
    }
  }

  // With no recording, the slides take the stage — so no separate Slides tab.
  const slidesAsStage = !recording && !!slides;
  const tabItems = [
    { id: 'overview', label: 'Class notes', icon: ChatText },
    ...(slides && !slidesAsStage ? [{ id: 'slides', label: 'Slides', icon: Monitor }] : []),
    ...(notes ? [{ id: 'notes', label: 'Notes', icon: NotePencil }] : []),
    ...(infographic ? [{ id: 'infographic', label: 'Infographic', icon: ChartBar }] : []),
    ...(incident ? [{ id: 'incident', label: 'Incident report', icon: ShieldWarning }] : []),
    ...(transcript ? [{ id: 'transcript', label: 'Full transcript', icon: FileText }] : []),
    ...(studyQuestions.length ? [{ id: 'practice', label: 'Practice', icon: Exam }] : []),
  ];

  return (
    <div className="space-y-6">
      <Link
        to={coursePath('')}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-brand-teal"
      >
        <CaretLeft size={16} weight="bold" /> Back to the curriculum
      </Link>
      <SectionHeading
        eyebrow={section ? section.title : 'Curriculum'}
        title={module.title}
        description={recording && recording.durationSec > 0 ? `About ${formatDuration(recording.durationSec)}` : undefined}
      />

      {module.learningGoals && module.learningGoals.length > 0 && (
        <Card className="p-5">
          <p className="label-mono text-brand-teal">By the end of this class, you'll be able to</p>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
            {module.learningGoals.map((g) => (
              <li key={g} className="flex gap-2">
                <CheckCircle size={18} weight="fill" className="mt-px shrink-0 text-brand-teal" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr,20rem]">
        {/* MAIN COLUMN */}
        <div className="min-w-0 space-y-5">
          {slidesAsStage && slides ? (
            <SlidesTab material={slides} title={module.title} />
          ) : (
            <RecordingStage recording={recording} title={module.title} hasSlides={!!slides} onWatch={reportWatch} />
          )}

          {/* Slim status row — the completion action lives at the end of the sequence. */}
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="teal">Class</Pill>
            {recording && recording.durationSec > 0 && (
              <span className="text-sm text-slate-500">{formatDuration(recording.durationSec)}</span>
            )}
            {materials.length > 0 && (
              <span className="text-sm text-slate-400">
                {materials.length} class resource{materials.length > 1 ? 's' : ''}
              </span>
            )}
            {completed && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle size={14} weight="fill" /> Completed
              </span>
            )}
          </div>

          {/* Review — class notes, slides, transcript, practice */}
          <Card className="p-0">
            <Tabs items={tabItems} active={tab} onChange={setTab} className="px-5 pt-1" />
            <div className="p-6">
              {tab === 'overview' && <OverviewTab module={module} summary={summary} quizId={quizId} labs={labs} />}
              {tab === 'slides' && slides && <SlidesTab material={slides} title={module.title} />}
              {tab === 'notes' && notes && <NotesTab material={notes} />}
              {tab === 'infographic' && infographic && <InfographicTab material={infographic} title={module.title} />}
              {tab === 'incident' && incident && <NotesTab material={incident} label="incident report" />}
              {tab === 'transcript' && transcript && <TranscriptTab material={transcript} />}
              {tab === 'practice' && <PracticeTab questions={studyQuestions} attempts={studyAttempts} />}
            </div>
          </Card>

          {/* Apply — the lab is the practical next step, placed in the flow (not a floating side link). */}
          {labs.length > 0 && (
            <Card className="p-5">
              <div className="label-mono text-brand-teal">Apply what you learned</div>
              <div className="mt-3 space-y-3">
                {labs.map((lab) => (
                  <div
                    key={lab.id}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal">
                      <Flask size={20} weight="bold" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Practical lab</div>
                      <div className="truncate font-semibold text-brand-navy">{lab.title}</div>
                    </div>
                    <Link to={coursePath(`labs/${lab.id}`)} className="btn-primary shrink-0">
                      Open the lab
                    </Link>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {quizId && (
            <Card className="flex flex-wrap items-center gap-3 p-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal">
                <ListChecks size={20} weight="bold" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Knowledge check</div>
                <div className="font-semibold text-brand-navy">Quiz for this class</div>
              </div>
              <Link to={coursePath(`assessments/${quizId}`)} className="btn-secondary shrink-0">
                Take the quiz
              </Link>
            </Card>
          )}

          {/* Finish — learner-controlled completion, after the learning activities. */}
          {completed ? (
            <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <CheckCircle size={18} weight="fill" /> You completed this class
              </span>
              <button type="button" className="btn-secondary" onClick={() => setFeedbackOpen(true)}>
                <Star size={18} weight="bold" /> Rate this class
              </button>
            </Card>
          ) : (
            <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
              <div>
                <div className="font-semibold text-brand-navy">Finished the recording, practice, and lab?</div>
                <div className="text-sm text-slate-500">Marking complete is up to you — do it when you’re ready.</div>
              </div>
              <button className="btn-primary shrink-0" onClick={markComplete} disabled={saving || materials.length === 0}>
                {saving ? <Spinner size={18} className="text-white" /> : <CheckCircle size={18} weight="bold" />}
                Mark class complete
              </button>
            </Card>
          )}
          {saveError && <p className="text-sm font-medium text-rose-600">{saveError}</p>}

          <ClassDiscussion moduleId={module.id} />
        </div>

        {/* RAIL: where you are in this section — previous / current / next, and a count. */}
        <aside className="lg:w-80">
          <Card className="p-5">
            <span className="label-mono">Section progress</span>
            <div className="mt-1 text-sm font-semibold text-brand-navy">
              {railDone} of {railClasses.length} class{railClasses.length === 1 ? '' : 'es'} complete
            </div>
            <div className="mt-2">
              <ProgressBar value={railDone} max={railClasses.length || 1} />
            </div>

            {showAllClasses ? (
              <ol className="mt-4 space-y-1">
                {railClasses.map((m) => {
                  const active = m.id === module.id;
                  const isDone = m.state === 'completed' || (active && completed);
                  return (
                    <li key={m.id}>
                      <button
                        onClick={() => !active && onNavigate(m.id)}
                        aria-current={active ? 'true' : undefined}
                        className={cx(
                          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition',
                          active ? 'bg-brand-teal/10 font-semibold text-brand-navy' : 'text-slate-600 hover:bg-slate-50',
                        )}
                      >
                        <span className="shrink-0">
                          {isDone ? (
                            <CheckCircle size={20} weight="fill" className="text-emerald-500" />
                          ) : active ? (
                            <VideoCamera size={20} weight="fill" className="text-brand-teal" />
                          ) : (
                            <Circle size={20} className="text-slate-300" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{m.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="mt-4 space-y-3">
                {prevClass && (
                  <button
                    onClick={() => onNavigate(prevClass.id)}
                    className="block w-full rounded-xl px-3 py-2 text-left transition hover:bg-slate-50"
                  >
                    <div className="label-mono text-slate-400">Previous</div>
                    <div className="mt-0.5 truncate text-sm text-slate-600">{prevClass.title}</div>
                  </button>
                )}
                <div className="rounded-xl bg-brand-teal/10 px-3 py-2">
                  <div className="label-mono text-brand-teal">Current</div>
                  <div className="mt-0.5 truncate text-sm font-semibold text-brand-navy">{module.title}</div>
                </div>
                {nextRailClass && (
                  <button
                    onClick={() => onNavigate(nextRailClass.id)}
                    className="block w-full rounded-xl px-3 py-2 text-left transition hover:bg-slate-50"
                  >
                    <div className="label-mono text-slate-400">Next</div>
                    <div className="mt-0.5 truncate text-sm text-slate-600">{nextRailClass.title}</div>
                  </button>
                )}
              </div>
            )}

            {railClasses.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllClasses((v) => !v)}
                className="mt-3 text-xs font-semibold text-brand-teal transition hover:underline"
              >
                {showAllClasses ? 'Show fewer' : `View all ${railClasses.length} classes`}
              </button>
            )}
          </Card>
        </aside>
      </div>

      <Modal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} title="How was this class?">
        <ClassFeedback moduleId={module.id} onClose={() => setFeedbackOpen(false)} />
      </Modal>
    </div>
  );
}

/** Seconds → "m:ss" (or "h:mm:ss" past an hour) for a chapter marker. */
function fmtChapterTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// A native <video> with a Teams-style chapter rail: click to seek, the current
// chapter highlights as it plays. Falls back to a bare player with no chapters.
function VideoWithChapters({
  src,
  title,
  chapters,
  onWatch,
}: {
  src: string;
  title: string;
  chapters: Chapter[];
  onWatch?: (sec: number) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [active, setActive] = useState(0);
  const ordered = [...chapters].sort((a, b) => a.startSec - b.startSec);
  const hasChapters = ordered.length > 0;

  // Watch tracking: remember the furthest second reached and report it at most
  // every ~15s of new progress (and on pause / ended / unmount), so reports get
  // "% watched" without a write on every timeupdate tick.
  const maxRef = useRef(0);
  const sentRef = useRef(0);
  const onWatchRef = useRef(onWatch);
  onWatchRef.current = onWatch;
  function flushWatch() {
    if (maxRef.current > sentRef.current) {
      sentRef.current = maxRef.current;
      onWatchRef.current?.(maxRef.current);
    }
  }
  useEffect(() => () => flushWatch(), []);

  function seek(startSec: number, i: number) {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = startSec;
    setActive(i);
    void v.play().catch(() => {});
  }

  function onTimeUpdate() {
    const t = videoRef.current?.currentTime ?? 0;
    if (t > maxRef.current) maxRef.current = t;
    if (hasChapters) {
      let idx = 0;
      for (let i = 0; i < ordered.length; i++) if (t >= ordered[i].startSec - 0.5) idx = i;
      setActive(idx);
    }
    if (maxRef.current - sentRef.current >= 15) flushWatch();
  }

  const video = (
    <video
      ref={videoRef}
      src={src}
      title={title}
      controls
      controlsList="nodownload"
      onTimeUpdate={onTimeUpdate}
      onPause={flushWatch}
      onEnded={flushWatch}
      className="aspect-video w-full rounded-2xl border border-slate-200 bg-black"
    />
  );
  if (!hasChapters) return video;

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_15rem]">
      <div>{video}</div>
      <div className="flex max-h-[380px] flex-col rounded-2xl border border-slate-200 bg-white lg:max-h-none">
        <div className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">
          <ListBullets size={14} weight="bold" /> Chapters
        </div>
        <ol className="flex-1 overflow-y-auto p-1">
          {ordered.map((c, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => seek(c.startSec, i)}
                className={cx(
                  'flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition',
                  i === active ? 'bg-brand-teal/10' : 'hover:bg-slate-50',
                )}
              >
                <span
                  className={cx(
                    'mt-px shrink-0 text-xs font-semibold tabular-nums',
                    i === active ? 'text-brand-teal' : 'text-slate-400',
                  )}
                >
                  {fmtChapterTime(c.startSec)}
                </span>
                <span className={cx('text-sm leading-snug', i === active ? 'font-semibold text-brand-navy' : 'text-slate-600')}>
                  {c.title}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ── Recording stage (16:9) ───────────────────────────────────────────────────
function RecordingStage({
  recording,
  title,
  hasSlides = false,
  onWatch,
}: {
  recording: Lesson | null;
  title: string;
  hasSlides?: boolean;
  onWatch?: (sec: number) => void;
}) {
  const url = recording?.streamUrl ?? '';
  const isFile = VIDEO_FILE_RE.test(url);
  const isSharePoint = /sharepoint\.com|microsoftstream|\.stream\./i.test(url);
  // Uploaded files always need a SAS; SharePoint links are resolved server-side
  // to a direct stream (kind 'video') or fall back to an embed (kind 'embed').
  const needsResolve = !!recording && (isFile || isSharePoint);
  const [resolved, setResolved] = useState<{ url: string; kind: string } | null>(null);
  const [resolveFailed, setResolveFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setResolved(null);
    setResolveFailed(false);
    if (recording && needsResolve) {
      api
        .lessonFile(recording.id)
        .then((r) => {
          if (!active) return;
          if (r.url) setResolved({ url: r.url, kind: r.kind ?? 'file' });
          else setResolveFailed(true);
        })
        .catch(() => active && setResolveFailed(true));
    }
    return () => {
      active = false;
    };
  }, [recording?.id, needsResolve]);

  if (recording?.streamUrl) {
    // Direct video: an uploaded file, or a SharePoint recording Graph resolved.
    const playAsVideo = isFile || (isSharePoint && resolved?.kind === 'video');
    if (playAsVideo && resolved) {
      return <VideoWithChapters src={resolved.url} title={title} chapters={recording.chapters ?? []} onWatch={onWatch} />;
    }
    // Still resolving (and not yet known to be an un-resolvable embed).
    if ((isFile || isSharePoint) && !resolved && !resolveFailed) {
      return (
        <div className="flex aspect-video w-full items-center justify-center rounded-2xl border border-slate-200 bg-black text-sm text-slate-300">
          Preparing video…
        </div>
      );
    }
    // SharePoint we couldn't resolve → embed iframe + Open fallback + sign-in hint.
    if (isSharePoint) {
      return (
        <div className="space-y-2">
          <div className="relative">
            <iframe
              src={toEmbedUrl(url)}
              title={title}
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              className="aspect-video w-full rounded-2xl border border-slate-200 bg-brand-navy"
            />
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in a new tab"
              className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-brand-navy shadow-sm backdrop-blur transition hover:bg-white"
            >
              <ArrowSquareOut size={16} weight="bold" /> Open
            </a>
          </div>
          <p className="text-xs text-slate-500">
            Hosted on SharePoint. Not playing?{' '}
            <a href={url} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-teal hover:underline">
              open it in a new tab
            </a>
            .
          </p>
        </div>
      );
    }
    // Other embeds (YouTube / Vimeo / Stream).
    return (
      <iframe
        src={toEmbedUrl(url)}
        title={title}
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        className="aspect-video w-full rounded-2xl border border-slate-200 bg-brand-navy"
      />
    );
  }

  return (
    <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-brand-navy/5 to-brand-teal/5 p-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-brand-teal shadow-sm">
        <VideoCamera size={28} weight="bold" />
      </span>
      <div>
        <p className="text-lg font-bold text-brand-navy">Recording coming soon</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
          {hasSlides
            ? 'The class recording will appear here once it’s published — the slides are below.'
            : 'The class recording will appear here once it’s published.'}
        </p>
      </div>
    </div>
  );
}

// ── Tab content ──────────────────────────────────────────────────────────────
function OverviewTab({
  module,
  summary,
  quizId,
  labs,
}: {
  module: Module;
  summary: Lesson | null;
  quizId: string | null;
  labs: Lab[];
}) {
  const { coursePath } = useCourse();
  return (
    <div className="space-y-5">
      {summary ? (
        <TextOrFileTab material={summary} label="class notes" rich />
      ) : (
        <p className="text-sm text-slate-500">The class summary will appear here once it's added.</p>
      )}
      {module.topics.length > 0 && (
        <div>
          <p className="label-mono mb-2">What you'll cover</p>
          <div className="flex flex-wrap gap-2">
            {module.topics.map((t) => (
              <Pill key={t} tone="slate">
                {t}
              </Pill>
            ))}
          </div>
        </div>
      )}
      {(quizId || labs.length > 0) && (
        <div className="flex flex-wrap gap-3 pt-1">
          {quizId && (
            <Link to={coursePath(`assessments/${quizId}`)} className="btn-secondary">
              <ListChecks size={18} weight="bold" /> Knowledge check
            </Link>
          )}
          {labs.map((lab) => (
            <Link key={lab.id} to={coursePath(`labs/${lab.id}`)} className="btn-secondary">
              <Flask size={18} weight="bold" /> {lab.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function DeckLoading() {
  return (
    <div className="flex h-[74vh] min-h-[440px] items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-900 text-sm text-slate-300">
      <Spinner size={26} className="text-white" /> Loading slides…
    </div>
  );
}

function SlidesTab({ material, title }: { material: Lesson; title: string }) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    api
      .lessonFile(material.id)
      .then((r) => active && setFileUrl(r.url))
      .catch(() => active && setFileUrl(null));
    return () => {
      active = false;
    };
  }, [material.id]);

  const src = material.streamUrl ?? '';
  if (!fileUrl) {
    return <p className="text-sm text-slate-500">Preparing your slides…</p>;
  }
  if (PDF_RE.test(src)) {
    return (
      <Suspense fallback={<DeckLoading />}>
        <PdfDeck url={fileUrl} title={title} />
      </Suspense>
    );
  }
  if (OFFICE_RE.test(src)) {
    return <SlideViewer src={officeEmbedUrl(fileUrl)} openUrl={fileUrl} title={title} />;
  }
  return (
    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
      <Monitor size={18} weight="bold" /> Open slide deck
    </a>
  );
}

/** Transcript / summary: pasted text renders directly; files get a viewer/link.
 *  `rich` runs the text through the markdown formatter (for class notes); the raw
 *  transcript keeps its plain, line-preserving layout. */
function TextOrFileTab({ material, label, rich = false }: { material: Lesson; label: string; rich?: boolean }) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const isFile = !!material.streamUrl;
  useEffect(() => {
    let active = true;
    if (isFile) {
      api
        .lessonFile(material.id)
        .then((r) => active && setFileUrl(r.url))
        .catch(() => active && setFileUrl(null));
    }
    return () => {
      active = false;
    };
  }, [material.id, isFile]);

  if (material.textContent) {
    return rich ? (
      <div className="max-w-[72ch]">
        <RichText text={material.textContent} />
      </div>
    ) : (
      <div className="max-w-[70ch] whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
        {material.textContent}
      </div>
    );
  }
  if (!isFile) return <p className="text-sm text-slate-500">No {label} yet.</p>;
  if (!fileUrl) return <p className="text-sm text-slate-500">Preparing the {label}…</p>;
  if (PDF_RE.test(material.streamUrl ?? '')) {
    return (
      <iframe
        src={`${fileUrl}#toolbar=0&navpanes=0&view=FitH`}
        title={label}
        className="h-[70vh] min-h-[420px] w-full rounded-2xl border border-slate-200 bg-white"
      />
    );
  }
  return (
    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
      <FileText size={18} weight="bold" /> Open the {label}
    </a>
  );
}

// Best-effort cleanup of Microsoft Teams transcript noise — filenames, "started
// transcription", recorded/organised-by lines, bare dates, empty lines. The raw
// text stays available behind "View original"; we never rewrite actual speech.
function cleanTranscript(raw: string): string {
  const noise = [
    /\.(mp4|m4a|mov|docx?|vtt|srt|txt)\s*$/i,
    /\b(started|stopped) transcription\b/i,
    /^recorded by\b/i,
    /^organi[sz]ed by\b/i,
    /^organi[sz]er[:\s]/i,
    /^meeting (started|ended)\b/i,
    /^transcript\b/i,
    /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b.*$/,
  ];
  return raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !noise.some((re) => re.test(l)))
    .join('\n');
}

/** Full transcript — a support resource: Teams noise trimmed, raw kept a click away. */
function TranscriptTab({ material }: { material: Lesson }) {
  const [showOriginal, setShowOriginal] = useState(false);
  if (!material.textContent) return <TextOrFileTab material={material} label="transcript" />;
  const cleaned = cleanTranscript(material.textContent);
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-brand-navy">Full class transcript</p>
        <p className="text-xs text-slate-500">
          Search the transcript (Ctrl or Cmd + F) or use it to revisit a specific explanation.
        </p>
      </div>
      <div className="max-w-[70ch] whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
        {showOriginal ? material.textContent : cleaned}
      </div>
      <button
        type="button"
        onClick={() => setShowOriginal((v) => !v)}
        className="text-xs font-semibold text-brand-teal transition hover:underline"
      >
        {showOriginal ? 'Hide original transcript' : 'View original transcript'}
      </button>
    </div>
  );
}

/** Study notes: instructor-authored rich text (formatted), or an uploaded file. */
function NotesTab({ material, label = 'notes' }: { material: Lesson; label?: string }) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const isFile = !material.textContent && !!material.streamUrl;
  useEffect(() => {
    let active = true;
    if (isFile) {
      api
        .lessonFile(material.id)
        .then((r) => active && setFileUrl(r.url))
        .catch(() => active && setFileUrl(null));
    }
    return () => {
      active = false;
    };
  }, [material.id, isFile]);

  if (material.textContent) {
    return (
      <div className="max-w-[72ch]">
        <RichText text={material.textContent} />
      </div>
    );
  }
  if (!isFile) return <p className="text-sm text-slate-500">No {label} yet.</p>;
  if (!fileUrl) return <p className="text-sm text-slate-500">Preparing the {label}…</p>;
  if (PDF_RE.test(material.streamUrl ?? '')) {
    return (
      <iframe
        src={`${fileUrl}#toolbar=0&navpanes=0&view=FitH`}
        title={label}
        className="h-[70vh] min-h-[420px] w-full rounded-2xl border border-slate-200 bg-white"
      />
    );
  }
  return (
    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
      <NotePencil size={18} weight="bold" /> Open the {label}
    </a>
  );
}

/** Infographic: an uploaded image (shown inline, click to enlarge) or a PDF. */
function InfographicTab({ material, title }: { material: Lesson; title: string }) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    api
      .lessonFile(material.id)
      .then((r) => active && setFileUrl(r.url))
      .catch(() => active && setFileUrl(null));
    return () => {
      active = false;
    };
  }, [material.id]);

  const src = material.streamUrl ?? '';
  if (!fileUrl) return <p className="text-sm text-slate-500">Preparing the infographic…</p>;
  if (IMAGE_RE.test(src)) {
    return (
      <div className="max-h-[80vh] overflow-auto rounded-2xl border border-slate-200 bg-white p-3">
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
          <img src={fileUrl} alt={`${title} infographic`} className="mx-auto block h-auto w-full max-w-3xl" />
        </a>
      </div>
    );
  }
  if (PDF_RE.test(src)) {
    return (
      <iframe
        src={`${fileUrl}#toolbar=0&navpanes=0&view=FitH`}
        title={`${title} infographic`}
        className="h-[78vh] min-h-[440px] w-full rounded-2xl border border-slate-200 bg-white"
      />
    );
  }
  return (
    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
      <ChartBar size={18} weight="bold" /> Open the infographic
    </a>
  );
}

// Tall embedded viewer for Office decks with a fullscreen control.
function SlideViewer({ src, openUrl, title }: { src: string; openUrl: string; title: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  return (
    <div className="relative w-full">
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        allow="fullscreen"
        allowFullScreen
        className="h-[74vh] min-h-[420px] w-full rounded-2xl border border-slate-200 bg-white"
      />
      <div className="absolute right-3 top-3 flex gap-2">
        <button
          type="button"
          onClick={() => frameRef.current?.requestFullscreen?.()}
          title="Fullscreen"
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy/85 px-3 py-1.5 text-xs font-semibold text-white shadow-sm backdrop-blur transition hover:bg-brand-navy"
        >
          <CornersOut size={16} weight="bold" /> Fullscreen
        </button>
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in a new tab"
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-brand-navy shadow-sm backdrop-blur transition hover:bg-white"
        >
          <ArrowSquareOut size={16} weight="bold" /> Open
        </a>
      </div>
    </div>
  );
}

// ── Practice: interview-style Q&A for self-study (answer → reveal) ───────────
function PracticeTab({
  questions,
  attempts,
}: {
  questions: StudyQuestionPublic[];
  attempts: StudyAttempt[];
}) {
  if (questions.length === 0) {
    return <p className="text-sm text-slate-500">No practice questions for this class yet.</p>;
  }
  // Latest answer per question, so a reload shows what you last wrote.
  const priorAnswer = new Map<string, { answer: string; at: string }>();
  for (const a of attempts) {
    const cur = priorAnswer.get(a.questionId);
    if (!cur || a.submittedAt > cur.at) priorAnswer.set(a.questionId, { answer: a.answer, at: a.submittedAt });
  }
  return (
    <div className="space-y-4">
      <p className="max-w-[70ch] text-sm leading-relaxed text-slate-600">
        Answer each question in your own words, like an interview — no AI marking, this is your own practice. Save your
        answer to keep it (your instructor can see who's practising); reveal the model answer any time to check yourself.
      </p>
      {questions.map((q, i) => (
        <PracticeQuestion
          key={q.id}
          index={i}
          question={q}
          initialAnswer={priorAnswer.get(q.id)?.answer ?? ''}
          alreadyPracticed={priorAnswer.has(q.id)}
        />
      ))}
    </div>
  );
}

function PracticeQuestion({
  index,
  question,
  initialAnswer,
  alreadyPracticed,
}: {
  index: number;
  question: StudyQuestionPublic;
  initialAnswer: string;
  alreadyPracticed: boolean;
}) {
  const [answer, setAnswer] = useState(initialAnswer);
  const [revealed, setRevealed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    const a = answer.trim();
    if (!a || saving) return;
    setSaving(true);
    try {
      await api.submitStudyAnswer(question.id, a);
      setSaved(true);
    } catch {
      /* non-fatal: the instructor just won't see this attempt */
    } finally {
      setSaving(false);
    }
  }

  function reveal() {
    setRevealed(true);
    void save(); // revealing also saves the current answer
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-brand-navy text-xs font-bold text-white">
          {index + 1}
        </span>
        <p className="text-sm font-semibold leading-relaxed text-brand-navy">{question.prompt}</p>
      </div>

      <textarea
        value={answer}
        onChange={(e) => {
          setAnswer(e.target.value);
          setSaved(false);
        }}
        rows={4}
        placeholder="Answer in your own words, like you would in an interview…"
        className="mt-3 w-full resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
      />

      {revealed ? (
        <div className="mt-3 rounded-xl bg-slate-50 p-4">
          <p className="label-mono mb-1">Model answer</p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{question.modelAnswer}</p>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button type="button" className="btn-primary" onClick={save} disabled={!answer.trim() || saving}>
            {saving ? <Spinner size={16} className="text-white" /> : <FloppyDisk size={16} weight="bold" />} Save my answer
          </button>
          <button type="button" className="btn-secondary" onClick={reveal}>
            <Eye size={16} weight="bold" /> Reveal model answer
          </button>
          {saved ? (
            <span className="text-xs font-medium text-emerald-600">Saved · you can return and improve this later.</span>
          ) : (
            alreadyPracticed && <span className="text-xs text-slate-400">Answered before</span>
          )}
        </div>
      )}
    </div>
  );
}

